<template>
  <div class="auth-result-container">
    <div class="result-card">
      <div class="result-icon" :class="iconClass">
        <van-icon :name="iconName" />
      </div>
      <div class="result-title">{{ statusTitle }}</div>
      <div class="result-desc">{{ statusDesc }}</div>

      <!-- 进行中：操作提示 -->
      <div class="result-tip" v-if="isPending">
        页面将每 {{ pollInterval / 1000 }} 秒自动刷新一次，无需手动操作
      </div>

      <!-- 失败时展示后端返回的具体原因 -->
      <div class="result-msg" v-if="showMsg">{{ record.msg }}</div>

      <div class="result-actions">
        <van-button
          type="info"
          class="btn-primary"
          :loading="loading"
          @click="refresh"
        >
          刷新状态
        </van-button>
        <van-button class="btn-secondary" @click="goHome">返回</van-button>
      </div>
    </div>
  </div>
</template>

<script>
  import caApi from '@/api/ca'

  // status 状态机
  // 0 人脸认证中 / 2 人工审核中 / 3 审核通过待制证  → 进行中，继续轮询
  // 1 认证失败 / 5 制证失败                           → 失败，终态
  // 4 制证成功 / 6 已绑定身份证号                      → 成功，终态
  const STATUS_LIST = [
    {
      status: 0,
      type: 'pending',
      title: '人脸认证中',
      desc: '请等待法人人脸认证完成，完成后将自动同步结果',
    },
    {
      status: 1,
      type: 'fail',
      title: '认证失败',
      desc: '很抱歉，本次CA认证未通过，您可以重新发起认证',
    },
    {
      status: 2,
      type: 'pending',
      title: '人工审核中',
      desc: '您的认证申请正在人工审核，请耐心等待',
    },
    {
      status: 3,
      type: 'pending',
      title: '审核通过，待制证',
      desc: '审核已通过，正在进行证书制作，请耐心等待',
    },
    {
      status: 4,
      type: 'success',
      title: 'CA认证成功',
      desc: '恭喜，企业CA认证已完成，可正常使用电子签署',
    },
    {
      status: 5,
      type: 'fail',
      title: '制证失败',
      desc: '证书制作失败，请重新发起认证或联系客服',
    },
    {
      status: 6,
      type: 'success',
      title: 'CA认证成功',
      desc: '企业身份信息已绑定，CA认证完成',
    },
  ]
  const PENDING = new Set([0, 2, 3])
  const FAIL = new Set([1, 5])
  const FAIL_TO_SHOW_MSG = new Set([0, 1, 5])

  export default {
    name: 'AuthResult',
    data() {
      return {
        loading: false,
        record: {},
        timer: null,
        pollInterval: 3000,
      }
    },
    computed: {
      // 第三方认证完成回跳携带 transactionId 参数
      id() {
        return this.$route.query.transactionId || ''
      },
      statusItem() {
        return (
          STATUS_LIST.find(it => it.status === this.record.status) || null
        )
      },
      isPending() {
        return PENDING.has(this.record.status)
      },
      isFail() {
        return FAIL.has(this.record.status)
      },
      statusTitle() {
        return (this.statusItem && this.statusItem.title) || '认证结果查询中'
      },
      statusDesc() {
        return (
          (this.statusItem && this.statusItem.desc) ||
          '正在为您查询CA认证结果，请稍候...'
        )
      },
      iconClass() {
        if (this.isPending) return 'is-pending'
        if (this.isFail) return 'is-fail'
        return 'is-success'
      },
      iconName() {
        if (this.isPending) return 'loading'
        if (this.isFail) return 'close'
        return 'checked'
      },
      showMsg() {
        return (
          this.record.msg &&
          FAIL_TO_SHOW_MSG.has(this.record.status) &&
          this.record.status !== 0
        )
      },
    },
    mounted() {
      if (!this.id) {
        this.$toast('缺少认证订单信息，无法查询结果')
        return
      }
      this.fetchStatus()
    },
    beforeDestroy() {
      this.clearTimer()
    },
    methods: {
      async fetchStatus() {
        this.loading = true
        try {
          const data = await caApi.queryCorpCaVerify({ id: this.id })
          this.record = data || {}
          if (this.isPending) {
            this.startPolling()
          } else {
            this.clearTimer()
            if (!this.isFail) {
              // 认证成功后刷新用户信息，同步企业/个人认证的最新状态
              this.$store.dispatch('fetchUserInfo')
            }
          }
        } catch (error) {
          this.clearTimer()
          console.error(error)
        } finally {
          this.loading = false
        }
      },
      startPolling() {
        this.clearTimer()
        this.timer = setInterval(() => {
          this.fetchStatus()
        }, this.pollInterval)
      },
      clearTimer() {
        if (this.timer) {
          clearInterval(this.timer)
          this.timer = null
        }
      },
      refresh() {
        this.fetchStatus()
      },
      goHome() {
        this.$router.replace({ name: 'My' })
      },
    },
  }
</script>

<style lang="less" scoped>
  .auth-result-container {
    min-height: 100vh;
    background: #f4f5f6;

    .result-card {
      margin: 0 auto;
      padding: 48px 16px;
      background: #fff;
      border-radius: 8px;
      text-align: center;
      min-height: 100vh;
    }

    .result-icon {
      width: 72px;
      height: 72px;
      margin: 0 auto 20px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 36px;
      color: #fff;

      .van-icon {
        font-weight: 600;
      }

      &.is-success {
        background: #18a058;
      }

      &.is-fail {
        background: #f53f3f;
      }

      &.is-pending {
        background: #1676ff;

        .van-icon {
          animation: rotating 1.5s linear infinite;
        }
      }
    }

    @keyframes rotating {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    .result-title {
      font-size: 20px;
      font-weight: 600;
      color: #1f2329;
      margin-bottom: 8px;
    }

    .result-desc {
      font-size: 14px;
      color: #4e5969;
      line-height: 1.6;
      padding: 0 12px;
    }

    .result-tip {
      margin-top: 12px;
      font-size: 12px;
      color: #8f959e;
    }

    .result-msg {
      margin: 12px 16px 0;
      padding: 10px 16px;
      background: #fff1f0;
      border: 1px solid #ffb4b0;
      border-radius: 6px;
      color: #8e2018;
      font-size: 13px;
      line-height: 1.6;
      text-align: left;
    }

    .result-actions {
      margin-top: 28px;
      display: flex;
      justify-content: center;
      gap: 12px;

      .van-button {
        height: 40px;
        padding: 0 24px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;
        min-width: 96px;
      }

      .btn-primary {
        background: #1676ff;
        border-color: #1676ff;
      }

      .btn-secondary {
        background: #fff;
        color: #1f2329;
        border-color: #c9cdd4;
      }
    }
  }
</style>
